import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import SiteNav from './SiteNav';
import Footer from './Footer';
import { useLanguage } from './LanguageContext';
import { translations } from './translations';
import hero1Image from './assets-optimized/hero1.webp';
import hero2Image from './assets/hero2.jpg';
import hero3Image from './assets/hero3.jpg';
import hero4Image from './assets/hero4.jpg';
import hero5Image from './assets/hero5.jpg';
import hero6Image from './assets/hero6.jpg';
import hero7Image from './assets/hero7.jpg';
import hero8Image from './assets/hero8.jpg';
import hero9Image from './assets/hero9.jpg';
import hero10Image from './assets/hero10.jpg';
import hero11Image from './assets/hero11.jpg';
import hero12Image from './assets/hero12.jpg';
import travelCover from './assets/gallery/travel/cover.jpg';
import portraitsCover from './assets/gallery/portraits/cover.jpg';
import streetCover from './assets/gallery/street/cover.jpg';
import './App.css';

const GALLERY_CATEGORIES = [
  { key: 'travel', cover: travelCover },
  { key: 'portraits', cover: portraitsCover },
  { key: 'street', cover: streetCover },
] as const;

const CAROUSEL_IMAGES = [
  hero2Image,
  hero3Image,
  hero4Image,
  hero5Image,
  hero6Image,
  hero7Image,
  hero8Image,
  hero9Image,
  hero10Image,
  hero11Image,
  hero12Image,
];

const POSITIVE_TEXT = 'POSITIVE';
const GRAIN_TEXT = 'GRAIN';

const APPEAR_STAGGER = 15; // ms between each POSITIVE letter fading in (mirrored)
const APPEAR_DURATION = 408; // ms per letter fade-in
const RESOLVE_OVERLAP = 119; // ms resolve starts before the LAST letter even finishes appearing
const RESOLVE_STAGGER = 64; // ms between each POSITIVE letter un-mirroring, left to right
const RESOLVE_DURATION = 553; // ms per letter's own flip
const RESOLVE_TOTAL_DURATION =
  (POSITIVE_TEXT.length - 1) * RESOLVE_STAGGER + RESOLVE_DURATION;
// The curtain sweeps the FULL screen width in this same span — the black
// background is entirely gone by the time POSITIVE finishes resolving.

const SHIFT_OVERLAP = 102; // ms shift starts before the last letter finishes resolving
const SHIFT_DURATION = 765; // ms for POSITIVE+photo to slide/grow and GRAIN to unfold — all one motion
const GRAIN_LETTER_REVEAL_DURATION = 255; // ms each GRAIN letter takes to reveal at its scheduled moment

const HOLD_AFTER_SHIFT = 85; // brief beat to let the zoomed/colorized hero read, then move on
const SETTLE_DURATION = 680; // ms for monogram migration (position travel)
const MONOGRAM_MARGIN = 44; // px from top-left corner
const LOGO_HANDOFF_DURATION = 220; // ms — how fast OPACITY fades once the
// letters start their journey to the corner. They travel there over the
// full SETTLE_DURATION like before, but become invisible much sooner
// (this duration), so by the time they'd physically arrive they're long
// gone — no collision with the static SVG logo fading in after them.
// CSS's transition-duration accepts one value per transition-property
// (see .loader-letter: opacity, transform, left, top, font-size, color) —
// this string gives opacity a short duration while position/size keep the
// original slow travel time.
const HANDOFF_TRANSITION_DURATION = `${LOGO_HANDOFF_DURATION}ms, ${SETTLE_DURATION}ms, ${SETTLE_DURATION}ms, ${SETTLE_DURATION}ms, ${SETTLE_DURATION}ms, ${SETTLE_DURATION}ms`;

const CAROUSEL_START_DELAY = 0; // carousel starts immediately after hero1 is ready
const CARD_INTERVAL = 143; // ms — constant, fast pace for every crossfade (no ramp)
function cardIntervalAt(_i: number, _total: number): number {
  return CARD_INTERVAL;
}
const LAST_IMAGE_HOLD = 298; // ms to hold on the last carousel image before dissolving
const DISSOLVE_DURATION = 595; // ms for the whole carousel layer to fade away, revealing PG

// Fallback aspect ratio (width / height) used until the real photo dimensions load
const FALLBACK_ASPECT = 3 / 2;
const GALLERY_FALLBACK_ASPECT = 3 / 4; // portrait default, until real cover dims load
const GALLERY_TILE_HEIGHT = 400; // px, fixed row height for the gallery section

// Static PG logo (vector outline, same file used site-wide via GlobalLogo).
// Once the intro's animated monogram finishes settling into place, we swap
// the live, font-rendered "P"/"G" letters for this instead — same fixed
// position/class as every other page (.gallery-page-logo), so the final
// resting logo is pixel-for-pixel the same element as on About/Contact/
// Gallery, is clickable, and is immune to any font-rendering jitter.
const LOGO_PATH_D =
  'm 10.64121,18.033008 v 8.904883 q 0,2.530078 0.27285,3.770312 0.29766,1.21543 1.0666,2.00918 0.66973,0.694531 1.53789,1.041797 0.89297,0.32246 2.33164,0.396875 v 0.570507 h -15.85019 v -0.570507 q 1.43867,-0.07442 2.30683,-0.396875 0.89297,-0.347266 1.5627,-1.041797 0.79375,-0.79375 1.0666,-2.00918 0.27285,-1.21543 0.27285,-3.770312 V 8.656836 q 0,-2.133203 -0.19844,-3.274219 -0.17363,-1.16582 -0.62011,-1.885156 -0.64492,-0.992187 -1.63711,-1.463476 -0.99219,-0.496094 -2.57969,-0.595313 h -0.17363 v -0.570508 h 14.95722 q 3.91914,0 5.7795,0.396875 1.86035,0.396875 3.39824,1.5875 3.52226,2.678907 3.52226,7.14375 0,4.539258 -3.57187,6.647656 -1.86035,1.091407 -4.51445,1.314649 -0.84336,0.07441 -4.06797,0.07441 z m 0,-0.79375 h 2.43086 q 2.45566,0 3.77031,-0.347266 1.33945,-0.37207 2.38125,-1.339453 2.43086,-2.207617 2.43086,-5.878711 0,-2.033984 -0.71934,-3.720703 -0.69453,-1.711523 -2.00918,-2.778125 -1.51308,-1.265039 -3.59668,-1.488281 -0.5705,-0.04961 -2.60449,-0.04961 h -2.08359 z m 49.78301,-16.371094 0.29765,10.790039 -0.57051,0.04961 q -1.71152,-5.506641 -4.39043,-8.185547 -2.6789,-2.703711 -6.47402,-2.703711 -2.95176,0 -5.08496,1.21543 -2.1084,1.190625 -3.94394,3.894336 -3.72071,5.531445 -3.72071,12.65039 0,3.398242 0.89297,6.424414 0.89297,3.001367 2.60449,5.308203 3.32383,4.464844 8.11114,4.464844 1.88515,0 3.54707,-0.79375 1.66191,-0.79375 2.72851,-2.182813 1.46348,-1.934765 1.46348,-4.588867 0,-2.455664 -1.33946,-3.54707 -1.31464,-1.091406 -4.36562,-1.091406 v -0.570508 h 15.82539 v 0.570508 q -2.18281,0.198437 -3.07578,0.620117 -0.86816,0.396875 -1.28984,1.389062 -0.47129,1.091407 -0.47129,4.340821 0,0.892968 0.0992,2.778124 l 0.0496,0.570508 q -0.94257,-0.347265 -1.38906,-0.347265 -0.5457,0 -1.38906,0.570507 -2.15801,1.488282 -5.01055,2.282032 -2.85254,0.79375 -6.02754,0.79375 -4.36562,0 -7.88789,-1.488282 -3.49746,-1.488281 -5.68027,-4.266406 -1.63711,-2.083593 -2.53008,-4.960937 -0.86816,-2.877344 -0.86816,-6.101953 0,-4.440039 1.5875,-8.259961 1.5875,-3.819922 4.46484,-6.300391 2.35645,-2.009179 5.43223,-3.100585 3.07578,-1.091407 6.49883,-1.091407 1.98437,0 3.67109,0.372071 1.71152,0.37207 3.62148,1.215429 l 1.46348,0.669727 q 0.5457,0.223242 0.99219,0.223242 0.99219,0 1.51308,-1.612305 z';

export default function Intro() {
  const { lang } = useLanguage();
  const categoryLabels = translations[lang].categories;
  const introLabels = translations[lang].intro;
  const [dims, setDims] = useState({ w: 1200, h: 800 });
  const [photoAspect, setPhotoAspect] = useState(FALLBACK_ASPECT);
  const [galleryAspects, setGalleryAspects] = useState<Record<string, number>>({});

  type LetterTarget = { x: number; y: number; fontSize: number };
  type Rect = { left: number; top: number; width: number; height: number };

  const [soloTargets, setSoloTargets] = useState<LetterTarget[] | null>(null); // POSITIVE centered alone
  const [finalTargets, setFinalTargets] = useState<LetterTarget[] | null>(null); // POSITIVE shifted left
  const [grainTargets, setGrainTargets] = useState<LetterTarget[] | null>(null);
  const [grainFractions, setGrainFractions] = useState<number[] | null>(null);
  const [patchGeom, setPatchGeom] = useState<{ soloTextLeft: number } | null>(null);
  const [monoTargets, setMonoTargets] = useState<{ pos: LetterTarget; grain: LetterTarget } | null>(null);
  const [photoGeom, setPhotoGeom] = useState<{ solo: Rect; zoom: Rect } | null>(null); // { solo, zoom }

  const [appearedCount, setAppearedCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [patchGrown, setPatchGrown] = useState(false);
  const [shifted, setShifted] = useState(false);
  const [settled, setSettled] = useState(false);
  const [bgVisible, setBgVisible] = useState(true);

  const [carouselIndex, setCarouselIndex] = useState(-1); // -1 = not started
  const [slotSrc, setSlotSrc] = useState<(string | null)[]>([null, null]); // two PERSISTENT img slots
  const [slotDuration, setSlotDuration] = useState<number[]>([CARD_INTERVAL, CARD_INTERVAL]);
  const [activeSlot, setActiveSlot] = useState(0); // which slot is currently faded in
  const [dissolved, setDissolved] = useState(false); // carousel layer fading away, PG revealed
  const [fontsReady, setFontsReady] = useState(false);
  const [logoReady, setLogoReady] = useState(false); // static SVG logo has taken over from the animated letters
  const [activeTileIndex, setActiveTileIndex] = useState<number | null>(null); // mobile only: which entry tile (Travel/Portraits/Street) is tap-expanded

  useEffect(() => {
    // Canvas text measurement (used below to position the P/G monogram
    // letters) uses whatever font is ACTUALLY available at the moment it
    // runs. If Baskervville hasn't finished downloading yet, the browser's
    // fallback serif is measured instead — giving a slightly wrong width,
    // which then visibly snaps into place once the real font loads and a
    // later effect run re-measures. Waiting for fonts.ready avoids ever
    // measuring with the wrong font in the first place.
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const update = () =>
      setDims({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Load the real photo dimensions so every phase matches its natural
  // aspect ratio — nothing ever gets cropped.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setPhotoAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = hero1Image;
  }, []);

  // Tracks whether each CAROUSEL_IMAGES[i] has actually finished
  // downloading - a ref (not state) since nothing needs to re-render
  // when this changes, it's only read at the moment each scheduled
  // reveal timer fires (see the carousel scheduling effect below).
  const carouselLoadedRef = useRef<boolean[]>(
    CAROUSEL_IMAGES.map(() => false)
  );
  const carouselLoadListenersRef = useRef<Array<(() => void) | null>>(
    CAROUSEL_IMAGES.map(() => null)
  );

  // Preload hero2-5 into the browser cache so the crossfade has no blank
  // flash the first time each image appears. Now also tracks REAL load
  // completion (carouselLoadedRef) instead of just firing requests and
  // trusting the fixed CARD_INTERVAL schedule to have been enough time -
  // on a slow connection or an empty cache (first visit, cleared site
  // data), CARD_INTERVAL (143ms) is nowhere near enough for an actual
  // photo to download, so several images in a row would still be
  // mid-download when their scheduled reveal fires; the slot's <img>
  // then has nothing to show and the crossfade appears "stuck" on
  // whichever earlier photo (or two) happened to load in time - exactly
  // the reported symptom, and why it varied by browser/cache state.
  useEffect(() => {
    CAROUSEL_IMAGES.forEach((src, i) => {
      const img = new Image();
      img.onload = () => {
        carouselLoadedRef.current[i] = true;
        carouselLoadListenersRef.current[i]?.();
      };
      img.src = src;
    });
  }, []);

  // Load each gallery cover's REAL aspect ratio, same approach as hero1 —
  // never crop to a fixed box, size the box from the photo itself.
  useEffect(() => {
    GALLERY_CATEGORIES.forEach(({ key, cover }) => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setGalleryAspects((prev: Record<string, number>) => ({
            ...prev,
            [key]: img.naturalWidth / img.naturalHeight,
          }));
        }
      };
      img.src = cover;
    });
  }, []);

  useEffect(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return;
    const fontSize = Math.max(32, Math.min(76, dims.w * 0.062));
    ctx.font = `600 ${fontSize}px Baskervville, serif`;

    const posWidths = POSITIVE_TEXT.split('').map(
      (ch) => ctx.measureText(ch).width
    );
    const grainWidths = GRAIN_TEXT.split('').map(
      (ch) => ctx.measureText(ch).width
    );
    const posTotalWidth = posWidths.reduce((a, b) => a + b, 0);
    const grainTotalWidth = grainWidths.reduce((a, b) => a + b, 0);
    const wordGap = fontSize * 0.35;
    const combinedWidth = posTotalWidth + wordGap + grainTotalWidth;

    // The photo is ALWAYS exactly as wide as whichever text it's paired
    // with (posTotalWidth solo, combinedWidth once GRAIN joins) — it can
    // never end up narrower than the text sitting on it. The previous
    // version shrank the photo's WIDTH whenever its natural height (at
    // full text-width) wouldn't fit above a fixed topMargin — which is
    // exactly what let the words overhang past the photo's edges on
    // short/wide viewports.
    //
    // The whole final "POSITIVE GRAIN" + photo block is centered
    // vertically in the viewport (slightly below exact center — reads
    // more balanced than dead-center) instead of anchored near the
    // bottom with the photo hugging the top edge, which read as
    // top-heavy/uncentered.
    const textGap = fontSize * 1.1; // photo's bottom edge to text's vertical center
    const textExtentBelowCenter = fontSize * 0.55; // rough visual weight below that center
    const verticalCenterY = dims.h * 0.53;

    const zoomWidth = combinedWidth;
    const zoomHeight = zoomWidth / photoAspect;
    const zoomBlockHeight = zoomHeight + textGap + textExtentBelowCenter;
    let zoomTop = verticalCenterY - zoomBlockHeight / 2;
    const minTop = dims.h * 0.03; // safety net for extreme short/wide viewports only
    if (zoomTop < minTop) zoomTop = minTop;

    const lineY = zoomTop + zoomHeight + textGap;

    // Solo: POSITIVE centered on its own
    const soloTextLeft = dims.w / 2 - posTotalWidth / 2;
    let soloCursor = soloTextLeft;
    const solo = posWidths.map((w) => {
      const x = soloCursor + w / 2;
      soloCursor += w;
      return { x, y: lineY, fontSize };
    });

    // Final: POSITIVE + GRAIN centered together
    const groupLeft = dims.w / 2 - combinedWidth / 2;
    let finalCursor = groupLeft;
    const finalPos = posWidths.map((w) => {
      const x = finalCursor + w / 2;
      finalCursor += w;
      return { x, y: lineY, fontSize };
    });
    finalCursor += wordGap;
    const grainLeft = finalCursor;
    const grainT = grainWidths.map((w) => {
      const x = finalCursor + w / 2;
      finalCursor += w;
      return { x, y: lineY, fontSize };
    });
    const grainFracs = grainT.map((t, i) => {
      const leftEdge = t.x - grainWidths[i] / 2;
      return (leftEdge - grainLeft) / grainTotalWidth;
    });

    setSoloTargets(solo);
    setFinalTargets(finalPos);
    setGrainTargets(grainT);
    setGrainFractions(grainFracs);
    setPatchGeom({ soloTextLeft });

    // Solo phase: same width-matches-text rule, bottom edge anchored to
    // the SAME lineY as the final phase — letters only slide
    // horizontally during the shift, never vertically, exactly like
    // before. The photo grows upward from this smaller box to the
    // taller "zoom" box above, which still reads as "zoom toward
    // camera" the same as the original design.
    const soloHeight = posTotalWidth / photoAspect;
    const soloTop = lineY - textGap - soloHeight;

    setPhotoGeom({
      solo: { left: soloTextLeft, top: soloTop, width: posTotalWidth, height: soloHeight },
      zoom: { left: groupLeft, top: zoomTop, width: zoomWidth, height: zoomHeight },
    });

    const monoFontSize = Math.max(22, Math.min(34, dims.w * 0.024));
    ctx.font = `600 ${monoFontSize}px Baskervville, serif`;
    const pWidth = ctx.measureText('P').width;
    const gWidth = ctx.measureText('G').width;
    const gap = monoFontSize * 0.12;
    const pX = MONOGRAM_MARGIN + pWidth / 2;
    const gX = MONOGRAM_MARGIN + pWidth + gap + gWidth / 2;
    const y = MONOGRAM_MARGIN;
    setMonoTargets({
      pos: { x: pX, y, fontSize: monoFontSize },
      grain: { x: gX, y, fontSize: monoFontSize },
    });
    // This effect ONLY computes geometry (positions/sizes). It intentionally
    // does NOT touch appearedCount/resolvedCount/shifted/settled — those are
    // owned exclusively by the timing effect below, so an aspect-ratio
    // update (photo finishing loading) can never restart the animation.
  }, [dims.w, dims.h, photoAspect, fontsReady]);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const alreadyPlayed = sessionStorage.getItem('introPlayed') === 'true';

    if (reducedMotion || alreadyPlayed) {
      setAppearedCount(POSITIVE_TEXT.length);
      setResolvedCount(POSITIVE_TEXT.length);
      setPatchGrown(true);
      setShifted(true);
      setSettled(true);
      setBgVisible(false);
      setDissolved(true);
      setLogoReady(true);
      return;
    }

    // Phase 1: POSITIVE appears, mirrored
    POSITIVE_TEXT.split('').forEach((_, i) => {
      timers.push(
        setTimeout(
          () => setAppearedCount((c: number) => Math.max(c, i + 1)),
          i * APPEAR_STAGGER
        )
      );
    });

    const lastAppear =
      (POSITIVE_TEXT.length - 1) * APPEAR_STAGGER + APPEAR_DURATION;
    const resolveStart = lastAppear - RESOLVE_OVERLAP;

    // Phase 2: POSITIVE resolves while the curtain sweeps to the full screen
    // width and the photo reveals in sync.
    POSITIVE_TEXT.split('').forEach((_, i) => {
      timers.push(
        setTimeout(
          () => setResolvedCount((c: number) => Math.max(c, i + 1)),
          resolveStart + i * RESOLVE_STAGGER
        )
      );
    });
    timers.push(setTimeout(() => setPatchGrown(true), resolveStart));

    const lastResolve = resolveStart + RESOLVE_TOTAL_DURATION;
    const shiftStart = lastResolve - SHIFT_OVERLAP;

    // Phase 3: POSITIVE shifts left, the photo grows+moves to its full-width
    // "zoom" target, and GRAIN unfolds in the space that opens up — all in
    // the SAME motion/timeline, driven purely by `shifted`.
    timers.push(setTimeout(() => setShifted(true), shiftStart));

    // Phase 4: hero2-8 crossfade through in a separate layer on top.
    // "POSITIVE GRAIN" stays exactly where it is throughout — it does NOT
    // collapse to the monogram until the carousel itself is done.
    const heroReadyTime = shiftStart + SHIFT_DURATION;
    const carouselStart = heroReadyTime + HOLD_AFTER_SHIFT + CAROUSEL_START_DELAY;
    let cursor = carouselStart;
    const cardIntervals = CAROUSEL_IMAGES.map((_, i) =>
      cardIntervalAt(i, CAROUSEL_IMAGES.length)
    );
    CAROUSEL_IMAGES.forEach((src, i) => {
      const duration = cardIntervals[i];
      const fireAt = cursor;
      const reveal = () => {
        carouselLoadListenersRef.current[i] = null; // prevent double-fire
        setActiveSlot((current: number) => {
          const next = current === 0 ? 1 : 0;
          setSlotSrc((prevSlots: (string | null)[]) => {
            const updated = [...prevSlots];
            updated[next] = src;
            return updated;
          });
          setSlotDuration((prevDur: number[]) => {
            const updated = [...prevDur];
            updated[next] = duration;
            return updated;
          });
          return next;
        });
        setCarouselIndex(i);
      };
      timers.push(
        setTimeout(() => {
          // We've now reached image i's own scheduled moment - any
          // EARLIER image that still hadn't loaded by its own turn is
          // superseded at this point. Cancel those pending listeners so
          // a late-arriving 'load' event can't retroactively reveal an
          // older photo after the animation has already moved past it.
          for (let j = 0; j < i; j += 1) {
            carouselLoadListenersRef.current[j] = null;
          }
          if (carouselLoadedRef.current[i]) {
            // Already downloaded by the time its slot came up (the
            // normal case on a fast connection/warm cache) - reveal
            // immediately, exactly like before this fix.
            reveal();
          } else {
            // Not ready yet - wait for the actual 'load' event instead
            // of showing nothing/stale content.
            carouselLoadListenersRef.current[i] = reveal;
          }
        }, fireAt)
      );
      cursor += duration;
    });

    // Phase 5: after a brief hold on the LAST image, the whole carousel
    // layer fades away in one go, revealing the page underneath as
    // "POSITIVE GRAIN" collapses into the PG monogram — end of intro.
    const carouselEnd = cursor;
    const dissolveStart = carouselEnd + LAST_IMAGE_HOLD;
    timers.push(
      setTimeout(() => {
        setDissolved(true);
        setSettled(true);
        setBgVisible(false);
        sessionStorage.setItem('introPlayed', 'true');
        window.dispatchEvent(new Event('introComplete'));
      }, dissolveStart)
    );

    // Phase 6: the "P"/"G" letters start their journey toward the logo's
    // corner (position travels over the full SETTLE_DURATION, same visual
    // migration as before) but their OPACITY fades out much faster
    // (LOGO_HANDOFF_DURATION) — see HANDOFF_TRANSITION_DURATION, which
    // gives opacity and position/size different durations within the same
    // CSS transition. By the time they'd physically reach the corner
    // they've been invisible for a while. The static SVG logo appears
    // right after that fade finishes (plus a small buffer) — well before
    // the travel itself completes — so there's never a moment where both
    // are visible on top of each other.
    timers.push(
      setTimeout(
        () => setLogoReady(true),
        dissolveStart + LOGO_HANDOFF_DURATION + 60
      )
    );

    return () => {
      timers.forEach(clearTimeout);
      carouselLoadListenersRef.current = carouselLoadListenersRef.current.map(
        () => null
      );
    };
  }, [dims.w, dims.h]);

  // Mobile only: tapping anywhere outside the currently expanded entry
  // tile collapses it back to the 3-strip layout, instead of leaving it
  // stuck open until the user taps it again to navigate away.
  useEffect(() => {
    if (activeTileIndex === null) return;
    const handleOutsideTap = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.gallery-tile')) {
        setActiveTileIndex(null);
      }
    };
    document.addEventListener('click', handleOutsideTap);
    return () => document.removeEventListener('click', handleOutsideTap);
  }, [activeTileIndex]);

  const skipIntro = () => {
    setAppearedCount(POSITIVE_TEXT.length);
    setResolvedCount(POSITIVE_TEXT.length);
    setPatchGrown(true);
    setShifted(true);
    setSettled(true);
    setBgVisible(false);
    setDissolved(true);
    setLogoReady(true);
    sessionStorage.setItem('introPlayed', 'true');
    window.dispatchEvent(new Event('introComplete'));
  };

  // Touch devices have no hover, so the desktop "hover expands, click
  // navigates" flow doesn't translate directly. On hover-capable devices
  // this handler does nothing at all — the Link's default click always
  // navigates immediately, exactly as before, since the tile is already
  // expanded by CSS :hover by the time a click happens. On touch devices
  // (matchMedia '(hover: none)'), the FIRST tap on a collapsed tile
  // expands it (via .is-active, mirrored from the desktop :hover rules
  // inside an @media (hover: none) block in App.css) and is prevented
  // from navigating; a SECOND tap on the now-expanded tile is let through
  // to navigate normally.
  const handleTileClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    index: number
  ) => {
    const isTouch = window.matchMedia('(hover: none)').matches;
    if (!isTouch) return;
    if (activeTileIndex !== index) {
      e.preventDefault();
      setActiveTileIndex(index);
    }
  };

  return (
    <div className="page">
      <div
        className="loader"
        style={{ pointerEvents: bgVisible ? 'auto' : 'none' }}
      >
        <div className={`loader-bg ${bgVisible ? '' : 'hidden'}`} />

        {!dissolved && (
          <button
            type="button"
            className="skip-intro-button"
            onClick={skipIntro}
          >
            {introLabels.skip}
          </button>
        )}

        {photoGeom && patchGeom && (
          <div
            className="photo-wrap photo-positive"
            style={{
              left: photoGeom.zoom.left,
              top: photoGeom.zoom.top,
              width: photoGeom.zoom.width,
              height: photoGeom.zoom.height,
              transformOrigin: '0 0',
              willChange: 'transform, filter',
              transform: shifted
                ? 'translate(0px, 0px) scale(1, 1)'
                : `translate(${photoGeom.solo.left - photoGeom.zoom.left}px, ${
                    photoGeom.solo.top - photoGeom.zoom.top
                  }px) scale(${photoGeom.solo.width / photoGeom.zoom.width}, ${
                    photoGeom.solo.height / photoGeom.zoom.height
                  })`,
              filter: shifted ? 'grayscale(0)' : 'grayscale(1)',
              opacity: dissolved ? 0 : 1,
              clipPath: patchGrown ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
              transition: shifted
                ? `transform ${SHIFT_DURATION}ms cubic-bezier(0.16, 1, 0.3, 1), filter ${SHIFT_DURATION}ms ease-out, opacity ${DISSOLVE_DURATION}ms ease`
                : `clip-path ${
                    (photoGeom.solo.width / dims.w) * RESOLVE_TOTAL_DURATION
                  }ms linear ${
                    (photoGeom.solo.left / dims.w) * RESOLVE_TOTAL_DURATION
                  }ms`,
            }}
          >
            <img src={hero1Image} alt="" className="photo-img" />
          </div>
        )}

        {patchGeom && (
          <div
            className="patch"
            style={{
              left: 0,
              top: 0,
              width: dims.w,
              height: '100%',
              transformOrigin: '0 0',
              willChange: 'transform',
              transform: `scaleX(${patchGrown ? 1 : 0})`,
              opacity: dissolved ? 0 : 1,
              transition: `transform ${RESOLVE_TOTAL_DURATION}ms linear, opacity ${DISSOLVE_DURATION}ms ease`,
            }}
          />
        )}

        {soloTargets &&
          finalTargets &&
          POSITIVE_TEXT.split('').map((ch, i) => {
            const isMonogram = i === 0;
            const pos =
              settled && isMonogram && monoTargets
                ? monoTargets.pos
                : shifted
                ? finalTargets[i]
                : soloTargets[i];

            const appeared = i < appearedCount;
            const resolved = i < resolvedCount;
            const mirrored = !resolved;
            // Fades out well before it physically arrives at the corner —
            // see HANDOFF_TRANSITION_DURATION below.
            const opacity = settled ? 0 : appeared ? 1 : 0;

            return (
              <span
                key={i}
                className={`loader-letter ${
                  resolved ? 'ink-dark' : 'ink-light'
                }`}
                style={{
                  left: pos.x,
                  top: pos.y,
                  fontSize: pos.fontSize,
                  opacity,
                  transitionDuration: !settled
                    ? !resolved
                      ? `${appeared ? RESOLVE_DURATION : APPEAR_DURATION}ms`
                      : `${SHIFT_DURATION}ms`
                    : HANDOFF_TRANSITION_DURATION,
                  transform: `translate(-50%, -50%) rotateY(${
                    mirrored ? 180 : 0
                  }deg)`,
                }}
              >
                {ch}
              </span>
            );
          })}

        {grainTargets &&
          grainFractions &&
          GRAIN_TEXT.split('').map((ch, i) => {
            const isMonogram = i === 0;
            const pos =
              settled && isMonogram && monoTargets
                ? monoTargets.grain
                : grainTargets[i];

            // Fades out before it physically arrives at the corner — same
            // handoff as P above.
            const opacity = settled ? 0 : shifted ? 1 : 0;
            const delay = Math.max(
              0,
              grainFractions[i] *
                (SHIFT_DURATION - GRAIN_LETTER_REVEAL_DURATION)
            );

            return (
              <span
                key={i}
                className="loader-letter ink-dark"
                style={{
                  left: pos.x,
                  top: pos.y,
                  fontSize: pos.fontSize,
                  opacity,
                  transitionDuration: !settled
                    ? `${GRAIN_LETTER_REVEAL_DURATION}ms`
                    : HANDOFF_TRANSITION_DURATION,
                  transitionDelay: settled ? '0ms' : `${delay}ms`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {ch}
              </span>
            );
          })}

        {/* Phase 4-5: postcard crossfade, then the box fades away at once,
            revealing PG underneath — end of intro. Positioned exactly where
            hero1 sits, so one photo visually replaces the other with no
            overlap/collision. */}
        {carouselIndex >= 0 && photoGeom && (
          <div
            className="postcard-stage"
            style={{
              left: photoGeom.zoom.left,
              top: photoGeom.zoom.top,
              width: photoGeom.zoom.width,
              height: photoGeom.zoom.height,
              opacity: dissolved ? 0 : 1,
              pointerEvents: dissolved ? 'none' : 'auto',
              transition: `opacity ${DISSOLVE_DURATION}ms ease`,
            }}
          >
            {[0, 1].map((slot) => (
              <img
                key={slot}
                src={slotSrc[slot] || undefined}
                alt=""
                className="postcard-img"
                style={{
                  opacity: slotSrc[slot] && activeSlot === slot ? 1 : 0,
                  transitionDuration: `${slotDuration[slot]}ms`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Static, clickable PG logo — same SVG/class used on every other
          page. Fades in at the exact spot the animated monogram settles
          into, right as the animated letters fade out (see logoReady). */}
      {logoReady && (
        <Link
          to="/"
          className="gallery-page-logo"
          aria-label="Positive Grain - strona główna"
          style={{
            opacity: logoReady ? 1 : 0,
            transition: `opacity ${LOGO_HANDOFF_DURATION}ms ease`,
          }}
        >
          {/* Height no longer set here in JS - it's now the SAME CSS rule
              (.gallery-page-logo svg in App.css) that GlobalLogo.tsx's
              logo uses, pinned to the shared --nav-font-size variable.
              This IS a separate component instance from GlobalLogo (it
              returns null on "/", so this one exists specifically for
              here), but the SIZE now comes from one single CSS class
              rule shared by both instances - no more risk of the two
              copies drifting apart the way they just did. */}
          <svg
            viewBox="0 0 66.005264 35.569923"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path fill="currentColor" d={LOGO_PATH_D} />
          </svg>
        </Link>
      )}

      {dissolved && <SiteNav />}

      {/* .gallery-body wraps BOTH the tile row and the mobile label row
          and is what carries flex:1 now (previously .gallery-section
          itself had flex:1, which meant a normal-flow sibling after it
          had zero space left to render into — the label row's earlier
          absolute-positioning fix worked around that instead of fixing
          it structurally; this wrapper actually fixes it: both children
          now share the SAME reserved space in ordinary document flow,
          nothing needs position:absolute or z-index guesswork). */}
      <div className="gallery-body">
        <section className="gallery-section">
          {GALLERY_CATEGORIES.map(({ key, cover }, i) => {
            const aspect = galleryAspects[key] || GALLERY_FALLBACK_ASPECT;
            const restWidth = GALLERY_TILE_HEIGHT * aspect;
            const isActive = activeTileIndex === i;
            return (
              <Link
                to={`/${key}`}
                className={`gallery-tile${isActive ? ' is-active' : ''}`}
                key={key}
                style={{ '--rest-width': `${restWidth}px` } as CSSProperties}
                onClick={(e) => handleTileClick(e, i)}
              >
                <img
                  src={cover}
                  alt=""
                  className="gallery-tile-img"
                  decoding="async"
                />
                {/* Desktop-only reveal (see .gallery-tile-overlay /
                    .gallery-tile-label in App.css, gated behind
                    @media(hover:hover)) — the original white
                    label-on-photo hover effect. Mobile hides this
                    entirely and uses the shared row below instead
                    (.gallery-mobile-label-row), since a per-tile
                    overlay caused the earlier rotation/pivot problems
                    on touch. */}
                <div className="gallery-tile-overlay">
                  <span className="gallery-tile-label">
                    {categoryLabels[key]}
                  </span>
                </div>
              </Link>
            );
          })}
        </section>

        {/* Mobile-only label row, BELOW the tile row (not overlaid on
            any photo). Always rendered so there's no mount/unmount
            flicker; hidden entirely on hover-capable devices, empty on
            mobile until a tile is active. */}
        <div className="gallery-mobile-label-row">
          <span
            className={`gallery-mobile-label${
              activeTileIndex !== null ? ' is-visible' : ''
            }`}
          >
            {activeTileIndex !== null
              ? categoryLabels[GALLERY_CATEGORIES[activeTileIndex].key]
              : ''}
          </span>
        </div>
      </div>

      {dissolved && <Footer />}
    </div>
  );
}